// ConfigEditModal.tsx
import React, { useState } from "react";
import { Configuracion, PricingItem } from "../../types";
import { Plus, Trash2, X } from "lucide-react";

interface ConfigEditModalProps {
    config: Configuracion;
    onClose: () => void;
    onSave: (updatedConfig: Configuracion) => void;
}

const getFrequencyLabel = (weeklySessions: number) => (
    weeklySessions === -1 ? 'Ilimitada' : `${weeklySessions}XSemana`
);

const ConfigEditModal: React.FC<ConfigEditModalProps> = ({ config, onClose, onSave }) => {
    const [updatedConfig, setUpdatedConfig] = useState<Configuracion>({
        ...config,
        tarifas: config.tarifas.map(t => ({ ...t }))
    });
    const [errors, setErrors] = useState<{ name?: string; tarifas?: string }>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUpdatedConfig({
            ...updatedConfig,
            [name]: value,
        });
    };

    const handleTarifaChange = (index: number, field: keyof PricingItem, value: number) => {
        const newTarifas = updatedConfig.tarifas.map((t, i) =>
            i === index ? { ...t, [field]: value } : t
        );
        setUpdatedConfig({ ...updatedConfig, tarifas: newTarifas });
    };

    const handleAddTarifa = () => {
        setUpdatedConfig((prev) => ({
            ...prev,
            tarifas: [...prev.tarifas, { weeklySessions: 1, price: 0 }],
        }));
    };

    const handleRemoveTarifa = (index: number) => {
        setUpdatedConfig((prev) => ({
            ...prev,
            tarifas: prev.tarifas.filter((_, i) => i !== index),
        }));
    };

    const validate = (): boolean => {
        const newErrors: { name?: string; tarifas?: string } = {};

        if (!updatedConfig.name.trim()) {
            newErrors.name = 'El nombre es obligatorio';
        }

        const sesiones = updatedConfig.tarifas.map((t) => t.weeklySessions);
        if (updatedConfig.tarifas.length === 0) {
            newErrors.tarifas = 'Añade al menos una tarifa';
        } else if (sesiones.some((s) => !Number.isInteger(s) || (s < 1 && s !== -1))) {
            newErrors.tarifas = 'Las sesiones deben ser enteros >= 1 o -1 para Ilimitada';
        } else if (new Set(sesiones).size !== sesiones.length) {
            newErrors.tarifas = 'No pueden repetirse las sesiones por semana';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave(updatedConfig);
        onClose();
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center overflow-y-auto h-full w-full p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Editar Configuración</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="max-h-[65vh] overflow-y-auto p-6">
                    {/* Nombre de Configuración */}
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Nombre de Configuración
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={updatedConfig.name}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Ej: Configuración Principal"
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                    </div>

                    {/* Tarifas */}
                    <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                Tarifas por Frecuencia
                            </label>
                            <button
                                type="button"
                                onClick={handleAddTarifa}
                                className="flex items-center gap-1 text-sm px-3 py-1 bg-brand-primary hover:brightness-90 text-white rounded"
                            >
                                <Plus size={14} /> Añadir frecuencia
                            </button>
                        </div>
                        {errors.tarifas && (
                            <p className="text-red-500 text-xs mb-2">{errors.tarifas}</p>
                        )}
                        <div className="space-y-2">
                            {updatedConfig.tarifas.map((tarifa, index) => (
                                <div key={index} className="border border-gray-300 rounded p-3">
                                    <div className="grid grid-cols-2 gap-3 items-end">
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                                Frecuencia
                                            </label>
                                            <select
                                                value={tarifa.weeklySessions}
                                                onChange={(e) => handleTarifaChange(index, 'weeklySessions', Number(e.target.value))}
                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value={1}>{getFrequencyLabel(1)}</option>
                                                <option value={2}>{getFrequencyLabel(2)}</option>
                                                <option value={3}>{getFrequencyLabel(3)}</option>
                                                <option value={4}>{getFrequencyLabel(4)}</option>
                                                <option value={5}>{getFrequencyLabel(5)}</option>
                                                <option value={6}>{getFrequencyLabel(6)}</option>
                                                <option value={-1}>{getFrequencyLabel(-1)}</option>
                                            </select>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                                    Precio (€)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={tarifa.price}
                                                    onChange={(e) => handleTarifaChange(index, 'price', Number(e.target.value))}
                                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    onFocus={(e) => e.target.select()}
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </div>
                                            <div className="flex items-end">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTarifa(index)}
                                                    className="p-2 text-red-500 hover:text-red-700"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {updatedConfig.tarifas.length === 0 && (
                            <p className="text-gray-400 text-sm text-center py-4">
                                Sin tarifas. Añade al menos una.
                            </p>
                        )}
                    </div>
                    {/* Nota: las políticas del portal + notificaciones viven en su propia
                        sección "Políticas del portal de clientes" (PortalPoliciesModal),
                        fuera de este modal de tarifas. */}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t">
                    <button 
                        onClick={onClose} 
                        className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded"
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={handleSave} 
                        className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded"
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfigEditModal;